import { ethers } from 'ethers'
import { SignatureServiceInterface } from '~/modules/Products/Domain/SignatureServiceInterface'
import { Action } from '~/modules/Products/Domain/Action'
import { Category } from '~/modules/Products/Domain/Category'
import { SignableRequest } from '~/modules/Products/Domain/SignableRequest'
import { VerifiableRequest } from '~/modules/Products/Domain/VerifiableRequest'

interface DomainInterface {
  name: string
  version: string
  chainId: number
}

interface RequestInterface {
  category: Category
  action: Action
  id: string
  account: string
}

/**
 * Service to sign and verify requests using EIP-712
 *
 * EIP-712 is typically used for smart contracts to verify a digital signature. In our case, it's our backend (not a
 * contract) the one which verifies the signature. However, this draft standard is very useful in our use case because
 * the user's wallet will show a message to sign that the user can understand. I.e. they'll see they're signing a mint
 * request for the Trophyverse in a given blockchain, with a particular address and token id; instead of an opaque
 * string that might be anything (and therefore less trustworthy) or a raw string.
 */
export class Eip712SignatureService implements SignatureServiceInterface {
  private domainName = 'Trophyverse'
  private domainVersion = '1'
  private mintTypes = {
    MintRequest: [
      { name: 'category', type: 'string' },
      { name: 'action', type: 'string' },
      { name: 'id', type: 'string' },
      { name: 'account', type: 'address' },
    ],
  }

  /**
   * Sign a request using the EIP-712 algorithm
   * @param request Request to sign
   * @returns Digital signature
   */
  async signRequest (request: SignableRequest): Promise<string> {
    const domain: DomainInterface = {
      name: this.domainName,
      version: this.domainVersion,
      chainId: request.networkId,
    }

    const requestToSign: RequestInterface = {
      category: request.category,
      action: request.action,
      id: request.id,
      account: request.account,
    }

    return await request.signer._signTypedData(domain, this.mintTypes, requestToSign)
  }

  /**
   * Verify a mint request
   * @param mintRequest Request to verify
   * @throws Error if cannot verify signature
   */
  async verifyRequest (request: VerifiableRequest): Promise<void> {
    const domain: DomainInterface = {
      name: this.domainName,
      version: this.domainVersion,
      chainId: request.networkId,
    }

    const requestToVerify: RequestInterface = {
      category: request.category,
      action: request.action,
      id: request.id,
      account: request.account,
    }

    const signerAddress = ethers.utils.verifyTypedData(
      domain,
      this.mintTypes,
      requestToVerify,
      request.signature
    )

    if (request.account !== signerAddress) {
      throw new Error()
    }
  }
}
